import { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { trackCompletedStep } from '../../helpers/checkoutTracking';
import { STEPS } from '../../constants/checkoutConstants';
import {
    completeWireTransfer,
    togglePaymentSaving,
    toggleWireFormSubmissionError,
} from '../../actions/checkoutPaymentActions';
import { hasAllowWireTransferInUrl } from '../../helpers/checkoutRedirects';
import { createFragmentContainer, graphql } from 'react-relay/legacy';
import pick from 'lodash.pick';

import CheckoutCTAButton from '../../components/shared/CheckoutCTAButton';
import CheckoutSaveAndContinue from '../../components/shared/actions/CheckoutSaveAndContinueAction';
import { FormattedMessage } from 'dibs-react-intl';
import { billingFields, getAddressErrors } from '../../helpers/checkoutForms';
import { tradeFirmUpdateEoriVat } from '../../mutations/TradeFirmUpdateEoriVatMutation';
import styles from './styles/CheckoutWireTransferSubmit.scss';

class CheckoutWireTransferSubmit extends Component {
    constructor(props) {
        super(props);
        this.onSubmit = this.onSubmit.bind(this);
        this.saveTradeFirmVatEori = this.saveTradeFirmVatEori.bind(this);
    }

    async saveTradeFirmVatEori(tradeFirmId) {
        const { onTogglePaymentSaving, eoriNumber, vatId } = this.props;
        try {
            onTogglePaymentSaving(true);
            await tradeFirmUpdateEoriVat(this.props.relay.environment, {
                serviceId: tradeFirmId,
                eoriNumber,
                vatId,
            });
            onTogglePaymentSaving(false);
        } catch {
            onTogglePaymentSaving(false);
        }
    }

    onSubmit(event) {
        event.preventDefault();
        const {
            onCompleteWireTransfer,
            onToggleWireFormSubmissionError,
            buyer,
            isBillingSameAsShipping,
            newBillingForm,
            shippingAddressId,
            eoriNumber,
            vatId,
        } = this.props;

        const tradeFirmId = buyer?.tradeFirm?.serviceId;
        const addressList = buyer?.addressBook?.addressList || [];
        const address = addressList.find(a => a.serviceId === shippingAddressId) || null;
        const billingAddress = isBillingSameAsShipping
            ? pick(address, billingFields)
            : newBillingForm;

        const addressErrors = getAddressErrors(billingAddress, true);

        if (addressErrors.isValid) {
            if (tradeFirmId && (eoriNumber || vatId)) {
                this.saveTradeFirmVatEori(tradeFirmId);
            }
            trackCompletedStep(STEPS.STEP_PAYMENT);
            onCompleteWireTransfer(billingAddress);
        } else {
            onToggleWireFormSubmissionError(true);
        }
    }

    render() {
        const { buyer, transaction, wireShowFailedSubmissionError, isPaymentSaving } = this.props;
        const isTradeOrVIPBuyer = buyer?.isVip || buyer?.isVerifiedTrade;
        const isPrivateOffer = transaction?.isPrivateOffer;
        const showSubmit = isTradeOrVIPBuyer || isPrivateOffer || hasAllowWireTransferInUrl();
        return (
            showSubmit && (
                <>
                    <CheckoutCTAButton
                        label={<CheckoutSaveAndContinue />}
                        marginTop
                        dataTn="wireTransfer-save-button"
                        onClick={this.onSubmit}
                        isSaving={isPaymentSaving}
                    />
                    {wireShowFailedSubmissionError && (
                        <div className={styles.formErrorNotice}>
                            <FormattedMessage
                                id="checkout.CheckoutWireTransferSubmit.errorMessage"
                                defaultMessage="Please correct errors to continue."
                            />
                        </div>
                    )}
                </>
            )
        );
    }
}

CheckoutWireTransferSubmit.propTypes = {
    intl: PropTypes.object,
    onCompleteWireTransfer: PropTypes.func.isRequired,
    buyer: PropTypes.object.isRequired,
    transaction: PropTypes.object,
    isBillingSameAsShipping: PropTypes.bool,
    isPaymentSaving: PropTypes.bool,
    wireShowFailedSubmissionError: PropTypes.bool,
    newBillingForm: PropTypes.object,
    shippingAddressId: PropTypes.string,
    eoriNumber: PropTypes.string,
    vatId: PropTypes.string,
    onTogglePaymentSaving: PropTypes.func.isRequired,
    onToggleWireFormSubmissionError: PropTypes.func.isRequired,
    relay: PropTypes.object,
};

const ConnectedCheckoutWireTransferSubmit = connect(
    ({ payment, transaction }) => {
        return {
            wireShowFailedSubmissionError: payment.wireShowFailedSubmissionError,
            shippingAddressId: transaction.shippingAddressId,
            isBillingSameAsShipping: payment.isBillingSameAsShipping,
            isPaymentSaving: payment.isPaymentSaving,
            newBillingForm: payment.newBillingForm,
            eoriNumber: payment.newEoriNumber,
            vatId: payment.newVatId,
        };
    },
    {
        onCompleteWireTransfer: completeWireTransfer,
        onToggleWireFormSubmissionError: toggleWireFormSubmissionError,
        onTogglePaymentSaving: togglePaymentSaving,
    }
)(CheckoutWireTransferSubmit);

export default createFragmentContainer(ConnectedCheckoutWireTransferSubmit, {
    buyer: graphql`
        fragment CheckoutWireTransferSubmit_buyer on User {
            isVip
            isVerifiedTrade: hasPermission(name: "B_NET_PRICE")
            addressBook {
                addressList {
                    serviceId
                    firstName
                    lastName
                    country
                    line1
                    line2
                    city
                    state
                    zipCode
                }
            }
            tradeFirm {
                serviceId
            }
        }
    `,
    transaction: graphql`
        fragment CheckoutWireTransferSubmit_transaction on Transaction {
            isPrivateOffer
        }
    `,
});
